// Client Internal Audit — report helpers shared by the auditor worksheet, the office job page and the report print
// page. SINGLE SOURCE for: grading, counts, the Results / Remarks wording, checklist picking, and the frozen
// snapshot each report PDF is printed from (Real = "client", Clean = "clean", Close-out = "closeout").
import { supabase } from './supabase'
import { standardsLabel, sgToday } from './service'

// CCIS client internal audits grade findings as Observation or NC only (no Major / Minor split).
export const RESULTS = ['Conform', 'Obs', 'NC', 'N/A']
export const FINDINGS = ['Obs', 'NC']
export const isFinding = r => FINDINGS.includes(r)
// Office names: Company report (marked, internal circulation) · Audit report (for the certification body, no marks).
export const KIND_LABEL = { client: 'Company report', clean: 'Audit report (for certification body)', closeout: 'Close-out report' }
export const REPORT_STATUS = { preparing: 'Preparing…', ready: 'Ready', error: 'Could not prepare' }

// Rows the auditor answers: questions, a) b) sub-items and Annex A controls. A clause heading with nothing under
// it is answered on the heading itself.
export function answerable(items) {
  const out = new Set()
  items.forEach((it, i) => {
    if (['question', 'sub', 'control'].includes(it.kind)) { out.add(it.id); return }
    if (it.kind === 'clause') {
      // 'note' rows ("To consider / To produce" guidance under a question) are shown but never graded — look past them.
      let j = i + 1
      while (items[j] && items[j].kind === 'note' && items[j].section === it.section) j++
      const next = items[j]
      // A heading whose own sub-clause follows (5.1 → 5.1.1) is not graded itself — its sub-clauses are.
      if (next && next.kind === 'clause' && next.section === it.section && it.clause_no && String(next.clause_no || '').startsWith(it.clause_no + '.')) return
      if (!next || next.kind === 'clause' || next.kind === 'section' || next.section !== it.section) out.add(it.id)
    }
  })
  return out
}

// The clause number each row belongs to (questions / sub-items inherit the clause above them).
export function clauseOf(items) {
  const map = {}
  let cur = ''
  for (const it of items) { if (it.clause_no) cur = it.clause_no; map[it.id] = cur }
  return map
}

export function countFindings(results) {
  const c = { nc: 0, obs: 0 }
  for (const r of results) { if (r === 'NC') c.nc++; else if (r === 'Obs') c.obs++ }
  return c
}
// Total findings in a counts object (reports sent before 2 Oct 2026 carry the old { major, minor, obs } shape).
export const totalFindings = c => c ? (+c.nc || 0) + (+c.major || 0) + (+c.minor || 0) + (+c.obs || 0) : 0

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
export function resultsSentence(c) {
  return `There ${totalFindings(c) === 1 ? 'is' : 'are'} ${plural(c.nc, 'NC', 'NC(s)')} & ${plural(c.obs, 'Observation', 'Observation(s)')} for this audit.`
}
export function conclusionText(c) {
  if (totalFindings(c) > 0) return 'The organization has demonstrated conformity with audit criteria with finding(s) to close out.'
  return 'The organization has demonstrated conformity with audit criteria.'
}

export const REMARKS = [
  'Refer to ANNEX I and close-out all NCs (if any) as per your Corrections and Corrective Actions procedure.',
  'Kindly ensure that audit findings are closed before your certification/surveillance audit. In addition, the Management Review & Continual Improvement Plan must be done before the external auditors come in.',
  'Please ensure close-outs and email us your evidences (if any) with reference to ANNEX I as per the schedule for CCIS’ review before your own internal audit / management review and the forthcoming Certification/surveillance audit.',
  'This audit was conducted under ISO 19011:2026 guidelines (A.6) sampling method.',
]
export const DEFINITIONS = [
  ['Nonconformity (NC)', 'Non-fulfilment of a requirement'],
  ['Observation (Obs)', 'Minor lapses to the requirements'],
]
function cleanResultsText(auditDate, standards) {
  return `The internal audit was conducted on ${longDate(auditDate)} against ${standardsLabel(standards)} using the ISO 19011:2026 sampling method. Audit findings, if any, have been communicated to the organisation for corrective action.`
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export function longDate(d) {
  if (!d) return '—'
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number)
  return `${dd} ${MONTHS[m - 1]} ${y}`
}
export function slashDate(d) {
  if (!d) return '—'
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number)
  return `${dd}/${m}/${y}`
}

// "ISO 9001:2015 & ISO 14001:2015 INTERNAL AUDIT REPORT"
export function reportTitle(standards) {
  const s = standardsLabel(standards)
  return `${s ? s + ' ' : ''}INTERNAL AUDIT REPORT`
}

// Pick the checklist(s) for a set of standards: an exact match (e.g. the QEHS integrated checklist), else one
// single-standard checklist per standard. Standards with no checklist are returned in `missing` (upload mode only).
export function pickChecklists(standards, checklists) {
  const want = [...new Set(standards || [])].sort()
  const active = (checklists || []).filter(c => c.active !== false)
  const same = (a, b) => a.length === b.length && a.every((x, i) => x === b[i])
  const newest = list => list.sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0) || String(b.created_at).localeCompare(String(a.created_at)))[0]
  const exact = newest(active.filter(c => same([...(c.standards || [])].sort(), want)))
  if (exact) return { lists: [exact], missing: [] }
  const lists = [], missing = []
  for (const s of want) {
    const one = newest(active.filter(c => (c.standards || []).length === 1 && c.standards[0] === s))
    if (one) lists.push(one); else missing.push(s)
  }
  return { lists, missing }
}

// Load checklist items for the given checklist ids, in print order.
export async function loadItems(ids) {
  if (!ids?.length) return []
  const out = []
  for (const id of ids) {
    const { data } = await supabase.from('ia_checklist_items').select('*').eq('checklist_id', id).order('item_order')
    out.push(...(data || []))
  }
  return out
}

// Error text from an edge function reply (supabase-js hides the body of non-2xx replies).
export async function iaAction(body) {
  const { data, error } = await supabase.functions.invoke('ia-actions', { body })
  if (error) {
    let msg = error.message
    try { const b = await error.context.json(); msg = b.error || msg } catch (e) { /* keep message */ }
    throw new Error(msg)
  }
  if (data?.error) throw new Error(data.error)
  return data
}

// ── Snapshots (frozen content each PDF is printed from) ──────────────────────────────────────────────────────
// header: { company, address, auditDate, timeOfAudit, location, mgmtRep, auditors:[names], refNo, standards,
//           signatures:[{name, signed_at, ip}] }
// lists:  [{ id, title, standards, items:[…] }]   responses: { [item_id]: { result, notes, closed_at, closed_note, closed_by } }
// The Audit report (for the certification body; kind 'clean') is the Company report with two differences only: the Results sentence carries no finding counts
// (and no conclusion), and the Obs / NC column is left blank. Worksheet mode only (an uploaded checklist carries
// its own marks).
export function buildSnapshot(kind, { header, mode, lists, responses, counts, conclusion, attachments, closeoutDate }) {
  const clean = kind === 'clean'
  return {
    kind, mode, header, issuedOn: sgToday(),   // preview date; the server stamps the real send date
    counts: clean ? null : counts,
    results: clean ? cleanResultsText(header.auditDate, header.standards) : resultsSentence(counts),
    conclusion: clean ? null : conclusion,
    remarks: REMARKS, definitions: DEFINITIONS,
    closeoutDate: kind === 'closeout' ? (closeoutDate || sgToday()) : null,
    attachments: mode === 'upload' && kind === 'client' ? (attachments || []) : [],
    annex: mode === 'upload' ? [] : (lists || []).map(l => ({
      title: l.title, standards: l.standards,
      rows: l.items.map(it => {
        const r = responses[it.id] || {}
        return { kind: it.kind, section: it.section, clause_no: it.clause_no, label: it.label, title: it.title, text: it.body,
          result: clean ? null : (r.result || null), notes: r.notes || null,
          // "Close-out (whom / date)" as the auditor typed it on the row; the Close-out report adds how each finding was
          // closed. Left blank on the Audit report for the certification body (it would point at the findings).
          closeout: clean ? null : [r.closeout_whom,
            ...(kind === 'closeout' && isFinding(r.result) ? [r.closed_note, r.closed_at ? `Closed ${slashDate(r.closed_at)}` : null, r.closed_by ? `Verified by ${r.closed_by}` : null] : []),
          ].filter(Boolean).join('\n') || null }
      }),
    })),
  }
}
